import React from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { useSelector } from 'react-redux';
import { RootState } from '../../context/rootReducer';
import { NoDataIndication } from './NoDataIndication';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
    EmailNotificationsGroupPropMap,
    EmailNotificationsGroupTitlePropMap
} from '../../lib/dictionaries/EmailNotificationsGroupDictionary';
import Button from 'react-bootstrap/Button';
import { showConfirmationDialog } from '../Commons';
import {
    CONFIRM_DELETE_EMAIL_NOTIFICATION_GROUP_MESSAGE,
    DO_NOT_REMOVE_EMAIL_NOTIFICATIONS_LIST,
    EMAIL_GROUP_TYPES,
    EVENT_STATUS_MAP
} from '../../lib/constants';
import { updateEmailNotificationGroupAsync } from '../../slices/admin/emailNotificationsSlice';
import { useAppDispatch } from '../../context/store';
import { useToggle } from 'react-use';
import { EmailGroupType, EventStatusType } from '../../lib/types';

type Props = {
    groupType: EmailGroupType;
    eventStatus: EventStatusType;
};

const styles = {
    buttonStyle: {
        backgroundColor: '#EBECFF',
        width: '90px',
        height: '30px',
        border: 'none',
        color: '#0036C8',
        fontSize: '9pt',
        fontWeight: '500',
        borderRadius: '4px',
        float: 'right' as const,
        marginRight: '8px',
        marginTop: '2px'
    },
    rowDeleteStyle: {
        color: '#0D4AFF',
        padding: 0,
        alignSelf: 'flex-start',
        textAlign: 'left' as const,
        fontSize: '14px'
    }
};

const EmailNotificationsGroupTable = ({ groupType, eventStatus }: Props): JSX.Element => {
    const dispatch = useAppDispatch();
    const [tableFilters, setTableFilters] = useToggle(false);
    const { emailNotificationsGroups, isGettingEmailNotificationsGroups } = useSelector(
        (state: RootState) => state.emailNotifications
    );
    const groupsByTypeAndStatus = emailNotificationsGroups
        .filter((data) => data.groupType === groupType && data[eventStatus] === 1)
        .sort((a, b) =>
            groupType === EMAIL_GROUP_TYPES.TEAM
                ? a.name.localeCompare(b.name)
                : a.extId.localeCompare(b.extId)
        );

    const handleDelete = async (row: any): Promise<void> => {
        let { id, openEvent, closeEvent, reopenEvent, endEvent } = row;

        const confirmed = await showConfirmationDialog({
            title: CONFIRM_DELETE_EMAIL_NOTIFICATION_GROUP_MESSAGE,
            icon: 'warning',
            iconColor: '#F29718'
        });

        const events = {
            open: !!openEvent,
            close: !!closeEvent,
            reopen: !!reopenEvent,
            end: !!endEvent
        };

        const status = EVENT_STATUS_MAP[eventStatus];
        events[status] = false;

        const request = { id, events };
        if (confirmed && id != null) {
            dispatch(updateEmailNotificationGroupAsync(request));
        }
    };

    const rowDelete = (value: any, row: any) => {
        return DO_NOT_REMOVE_EMAIL_NOTIFICATIONS_LIST.includes(row?.extId) ? null : (
            <Button
                title='Delete'
                style={styles.rowDeleteStyle}
                variant='link'
                onClick={() => {
                    handleDelete(row);
                }}
            >
                Delete
            </Button>
        );
    };

    const getLocalTime = (cell: any) => {
        if (cell == null || cell === '') {
            return '';
        }
        const d = new Date(cell);
        if (Number.isNaN(d.getTime())) {
            return String(cell);
        }
        return d.toLocaleString();
    };

    const columns = {
        email: {
            dataField: EmailNotificationsGroupPropMap.email,
            text: EmailNotificationsGroupTitlePropMap.email,
            sort: true
        },
        dateLastModified: {
            dataField: EmailNotificationsGroupPropMap.dateLastModified,
            text: EmailNotificationsGroupTitlePropMap.dateLastModified,
            formatter: getLocalTime
        },
        user: {
            dataField: EmailNotificationsGroupPropMap.user,
            text: EmailNotificationsGroupTitlePropMap.user
        },
        remove: {
            dataField: EmailNotificationsGroupPropMap.remove,
            text: EmailNotificationsGroupTitlePropMap.remove,
            headerClasses: tableFilters ? 'non-filterable-column' : undefined,
            formatter: rowDelete
        },
        name: {
            dataField: EmailNotificationsGroupPropMap.name,
            text: EmailNotificationsGroupTitlePropMap.name,
            sort: true
        },
        abacusId: {
            dataField: EmailNotificationsGroupPropMap.abacusId,
            text: EmailNotificationsGroupTitlePropMap.abacusId,
            sort: true
        }
    };

    const renderedColumns =
        groupType === EMAIL_GROUP_TYPES.EMAIL_LIST
            ? [columns.email, columns.dateLastModified, columns.user, columns.remove]
            : [columns.name, columns.abacusId, columns.dateLastModified, columns.user, columns.remove];

    return (
        <div id='Overrides' className='mt-4'>
            <ToolkitProvider
                keyField={EmailNotificationsGroupPropMap.extId}
                data={groupsByTypeAndStatus}
                columns={renderedColumns}
            >
                {(props: any) => {
                    return (
                        <BootstrapTable
                            {...props.baseProps}
                            bootstrap4
                            bordered={false}
                            hover
                            wrapperClasses='table-responsive'
                            keyField={EmailNotificationsGroupPropMap.extId}
                            data={groupsByTypeAndStatus}
                            columns={renderedColumns}
                            noDataIndication={NoDataIndication(isGettingEmailNotificationsGroups)}
                        />
                    );
                }}
            </ToolkitProvider>
        </div>
    );
};

export default EmailNotificationsGroupTable;
